import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getFontFamilyClass, getFontFamilyStyle } from "../../core/utils/characterStyleUtils";
import { apiFetch } from "../../core/utils/api";
import useSpacing from "../../core/hooks/useSpacing";
import useBaseStyle from "../../core/hooks/useBaseStyle";
import useImageStyle from "../../core/hooks/useImageStyle";
import useContainerStyle from "../../core/hooks/useContainerStyle";
import { getBackgroundPatternClass } from "../../core/utils/backgroundPatternUtils";
import { getContainerInsetShadowClass } from "../../core/utils/containerStyleUtils";
import Background from "../components/Background";
import EditableElement from "../../editor/components/EditableElement";
import Item from "../components/Item";
import Text from "../components/Text";

const DEFAULT_SUCCESS_MESSAGE = "Your message has been sent successfully";
/** After submit, always use this label — the CMS submit `Item` still reads "Send". */
const SEND_ANOTHER_LABEL = "Send another message";
const LEGACY_BUTTON_CLASS =
  "rounded-full px-8 py-2 border border-gray-300 shadow-sm hover:shadow-md transition-all duration-300";

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

// Keep these fields EXACTLY the same as the legacy FooterForm.
const FORM_FIELDS = [
  { name: "name", type: "text", placeholder: "Name", required: true, colSpan: 1 },
  { name: "phone", type: "tel", placeholder: "Phone", required: false, colSpan: 1 },
  { name: "email", type: "email", placeholder: "Email", required: true, colSpan: 2 },
  { name: "message", type: "textarea", placeholder: "Message", required: true, colSpan: 2, rows: 4 },
];

const INITIAL_VALUES = { name: "", email: "", phone: "", message: "" };

const FIELD_CLASS =
  "border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-gray-400 bg-white text-black";

const FooterForm = ({ footer = {}, isMobile = false, containerId, disableSubmit = false }) => {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const formComponentId = footer?.form_component_id;
  const submitButtonId = footer?.submit_button_id;
  const hasNewSubtree = !!formComponentId && !!submitButtonId;

  const {
    data: formComponent,
  } = useQuery({
    queryKey: ["component", formComponentId],
    queryFn: async () => {
      const response = await apiFetch(`/api/components/${formComponentId}`);
      if (!response.ok) {
        throw new Error(`Error fetching component: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!formComponentId,
    staleTime: 30 * 1000,
    keepPreviousData: true,
  });

  const safeComponent = formComponent || null;

  const { margin: marginClasses, padding: paddingClasses } = useSpacing(safeComponent);
  const { styleClasses: baseClasses, inlineStyles: baseInlineStyles } = useBaseStyle(safeComponent);
  const containerStyleClasses = useContainerStyle(safeComponent);
  const insetShadowClass = getContainerInsetShadowClass(safeComponent?.container_style);
  const { styleClasses: imageStyleClasses, inlineStyles: imageInlineStyles } = useImageStyle(
    safeComponent?.background?.background_image,
  );

  const showImage = safeComponent?.background?.show_image;
  const bgImage =
    showImage && safeComponent?.background?.background_image?.image
      ? safeComponent.background.background_image.image
      : null;
  const bgPattern = safeComponent?.background?.background_pattern;
  const { colorClass = "", colorStyle = {}, jsPatternLayer = null } =
    bgImage ? {} : getBackgroundPatternClass(bgPattern);

  const hasRoundedBorder = /\brounded(-(?!none)\S+)?\b/.test(containerStyleClasses || "");
  const overflowClass = hasRoundedBorder ? "overflow-hidden" : "overflow-visible";

  const containerClasses = useMemo(
    () =>
      [
        // Layout constraints
        "max-w-2xl",
        "mx-auto",
        // Component styling system
        "relative",
        "group/component",
        "w-full",
        paddingClasses,
        baseClasses,
        containerStyleClasses,
        overflowClass,
      ]
        .filter(Boolean)
        .join(" "),
    [baseClasses, paddingClasses, containerStyleClasses, overflowClass],
  );

  const fontFamilyClass = getFontFamilyClass(footer.font_family);
  const fontFamilyStyle = getFontFamilyStyle(footer.font_family);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (disableSubmit) {
      // Editor safety: never send messages while editing.
      return;
    }

    // Don't submit if already submitting or no container ID
    if (isSubmitting || !containerId) return;

    setIsSubmitting(true);
    setError("");

    try {
      const response = await apiFetch(`/api/containers/${containerId}/contact/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          phone: values.phone,
          message: values.message,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setValues(INITIAL_VALUES);
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to send message. Please try again.");
      }
    } catch (err) {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setError("");
    setValues(INITIAL_VALUES);
  };

  const renderFormField = (field) => {
    const spanClass = field.colSpan === 2 && !isMobile ? "col-span-2" : "";

    if (field.type === "textarea") {
      return (
        <textarea
          key={field.name}
          name={field.name}
          rows={field.rows}
          placeholder={field.placeholder}
          aria-label={field.placeholder || field.name}
          value={values[field.name]}
          onChange={handleChange}
          required={field.required}
          className={`col-span-1 ${FIELD_CLASS} ${spanClass}`}
        />
      );
    }

    return (
      <input
        key={field.name}
        name={field.name}
        type={field.type}
        placeholder={field.placeholder}
        aria-label={field.placeholder || field.name}
        value={values[field.name]}
        onChange={handleChange}
        required={field.required}
        className={`${FIELD_CLASS} ${spanClass}`}
      />
    );
  };

  const gridClass = `grid gap-4 ${isMobile ? "grid-cols-1" : "grid-cols-2"}`;

  // Rendering safety: we can still render the form fields without a containerId (edit mode),
  // but we never submit unless containerId exists and disableSubmit=false.
  const introText = footer?.form_text ? (
    <div className="mb-4">
      {/* Render via Item so the footer-owned Text is selectable/editable in the editor */}
      <Item
        item={footer.form_text}
        itemRef={{ type: "text", id: footer.form_text?.id }}
        disableQuickDelete={true}
      />
    </div>
  ) : (
    !submitted &&
    footer?.form_introduction_text && <p className="mb-4">{footer.form_introduction_text}</p>
  );

  const successMessage = footer.form_success_message || DEFAULT_SUCCESS_MESSAGE;
  const successTextItem = footer?.form_text
    ? {
        ...footer.form_text,
        content: successMessage,
        // Text renderer expects HTML, so escape plain content and preserve line breaks.
        rendered_content: escapeHtml(successMessage).replace(/\n/g, "<br />"),
      }
    : null;

  const submitButtonEl = hasNewSubtree ? (
    <div aria-disabled={disableSubmit}>
      <Item itemRef={{ type: "button", id: submitButtonId }} disableQuickDelete={true} />
    </div>
  ) : (
    <button
      type="submit"
      className={LEGACY_BUTTON_CLASS}
      aria-disabled={disableSubmit}
      onClick={(e) => {
        if (disableSubmit) e.preventDefault();
      }}
    >
      Send
    </button>
  );

  const formInner = !submitted ? (
    <form
      onSubmit={handleSubmit}
      className={`relative z-10 ${fontFamilyClass}`}
      style={fontFamilyStyle}
    >
      {introText}

      <div className={gridClass}>{FORM_FIELDS.map(renderFormField)}</div>

      {error && (
        <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-md">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div className="mt-4 flex justify-center">{submitButtonEl}</div>
    </form>
  ) : (
    <div
      className={`relative z-10 text-center ${fontFamilyClass}`}
      style={fontFamilyStyle}
    >
      <div className="mb-4">
        {successTextItem ? <Text item={successTextItem} /> : successMessage}
      </div>
      <div className="flex justify-center">
        <button type="button" onClick={handleReset} className={LEGACY_BUTTON_CLASS}>
          {SEND_ANOTHER_LABEL}
        </button>
      </div>
    </div>
  );

  // New behavior: style the container using Footer.form_component (Component styling system).
  if (hasNewSubtree && safeComponent?.id) {
    return (
      <EditableElement element={safeComponent} disableQuickDelete={true}>
        {/* 
          IMPORTANT: keep margin classes off the `w-full` container.
          `w-full` + horizontal margins causes overflow and makes the element look shifted.
          Apply margins on an outer wrapper (auto width) and keep `w-full` on the inner box.
        */}
        <div className="w-full">
          <div className={marginClasses}>
            <div className={containerClasses} style={baseInlineStyles}>
              <Background
                bgImage={bgImage}
                imageStyleClasses={imageStyleClasses}
                imageInlineStyles={imageInlineStyles}
                colorClass={colorClass}
                colorStyle={colorStyle}
                jsPatternLayer={jsPatternLayer}
                hoverClass={insetShadowClass}
              />
              <div className="relative z-10">{formInner}</div>
            </div>
          </div>
        </div>
      </EditableElement>
    );
  }

  // Backwards-compat fallback: keep rendering even if subtree is missing.
  return (
    <div
      className={`w-full max-w-2xl mx-auto ${fontFamilyClass}`}
      style={fontFamilyStyle}
    >
      <div className="bg-white bg-opacity-80 p-6 rounded-md shadow-md">{formInner}</div>
    </div>
  );
};

export default FooterForm;
